import { Button } from "components/DesignSystem/Button/Button";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { MMM_YYYY, MONTH_FORMAT, YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { ModalControl } from "hooks/useModal";
import * as Yup from "yup";

export type StatementDateRange = {
  startDate: string;
  endDate: string;
};

type RefreshStatementsModalProps = {
  isLoading: boolean;
  onConfirm: (dateRange: StatementDateRange) => void;
} & ModalControl;

type FormValues = {
  start_month: string;
  end_month: string;
};

const validationSchema = Yup.object().shape({
  start_month: Yup.string().required("From month is required"),
  end_month: Yup.string().required("To month is required"),
});

export const RefreshStatementsModal = ({
  isOpen,
  close,
  onConfirm,
  isLoading,
}: RefreshStatementsModalProps) => {
  const today = new Date();

  const handleSubmit = ({ start_month, end_month }: FormValues) => {
    const endOfMonth = dayjs(end_month).endOf("month");
    const endDate = endOfMonth.isAfter(today) ? dayjs(today) : endOfMonth;

    onConfirm({
      startDate: dayjs(start_month).startOf("month").format(YYYY_MM_DD),
      endDate: endDate.format(YYYY_MM_DD),
    });
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay asChild>
        <FormikForm<FormValues>
          initialValues={{ start_month: "", end_month: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <>
              <Modal.Header>
                <Modal.Title>Refresh statements</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-gap-4">
                <div className="t-flex-1">
                  <DateField
                    name="start_month"
                    label="From"
                    required
                    showMonthYearPicker
                    dateFormat={MONTH_FORMAT}
                    placeholder={MMM_YYYY}
                    portalId="refresh-statements-start"
                    maxDate={
                      values.end_month ? new Date(values.end_month) : today
                    }
                  />
                </div>
                <div className="t-flex-1">
                  <DateField
                    name="end_month"
                    label="To"
                    required
                    showMonthYearPicker
                    dateFormat={MONTH_FORMAT}
                    placeholder={MMM_YYYY}
                    portalId="refresh-statements-end"
                    maxDate={today}
                    minDate={
                      values.start_month
                        ? new Date(values.start_month)
                        : undefined
                    }
                  />
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button type="button" onClick={close} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  Refresh
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
